import React from 'react';
import * as Cons from 'data/Consignment';
import { MODIFIED_BY_CUSTOMER } from 'constants/consignmentStates';
import OrderSummary from './OrderSummary';
import OrderAddonTablePreview from './OrderAddonTablePreview';
import { parseConsignmentId, getGroupedConsignmentEntries, isPricingUpdatingFromHybris } from 'utils/consignment';
import { TEXT_LABELS } from 'constants/order';
import FlexboxContainerComponent from 'components/UI/FlexboxContainer';
import Button from 'components/UI/Button';
import { printComponent } from 'utils/print';
import styled, { css } from 'styled-components';
import Icon from 'view/Icon';
import { Text } from 'styles/themeColors';
import { formatDateTime } from 'utils/formatters';

interface Props {
    consignmentDetailsResponse: Cons.ConsignmentDetails;
    isModalView?: boolean;
}

const PrintIcon = styled(Icon).attrs({ name: 'Print', size: 30 })``;

const TableColumn = styled.span<any>`
  font-weight: 400;
  font-size: 16px;
  line-height: 18px;
  min-height: 18px;
  width: 234px;
  color: ${Text.secondary};
  margin-bottom: 4px;

  ${({ color }) => color && css`
  color: ${color};
`}

${({ textDecoration }) => textDecoration && css`
  text-decoration: ${textDecoration};
`}

${({ fontSize }) => fontSize && css`
  font-size: ${fontSize};
`}

${({ fontWeight }) => fontWeight && css`
  font-weight: ${fontWeight};
`}

  ${({ deleted }) => deleted && css`
  opacity: 0.5;
`}

${({ width }) => width && css`
  width: ${width};
`}
`;

const showRentDates = (rentalStartDate: string, rentalEndDate: string) => (<>

    <FlexboxContainerComponent alignItems='center'>
        <TableColumn color={Text.secondary} width={'100%'}>Start date : {formatDateTime(rentalStartDate, false, false)}</TableColumn>
        {/* <TableColumn color={Text.primary} width={'134px'} fontWeight={'bold'} >{formatDateTime(rentalStartDate, false, false)}</TableColumn> */}
    </FlexboxContainerComponent>
    <FlexboxContainerComponent alignItems='center'>
        <TableColumn color={Text.secondary} width={'100%'}>End date : {formatDateTime(rentalEndDate, false, false)}</TableColumn>
        {/* <TableColumn color={Text.primary} width={'134px'} fontWeight={'bold'} >{formatDateTime(rentalEndDate, false, false)}</TableColumn> */}
    </FlexboxContainerComponent>

</>);

const OrderSnapshotPreview: React.FC<Props> = ({
    consignmentDetailsResponse,
    isModalView = false,
}) => {
    const submittedEntries = [...consignmentDetailsResponse?.consignmentEntries!, ...consignmentDetailsResponse?.consignmentEntriesAddons!];
    const originalEntries = consignmentDetailsResponse?.consignmentSnapshotEntries || [];

    const groupedSubmittedEntries = getGroupedConsignmentEntries(submittedEntries);
    const groupedOriginalEntries = getGroupedConsignmentEntries(originalEntries);

    const consignmentSnapshot = consignmentDetailsResponse?.consignmentSnapshot;
    const isPriceUpdating = isPricingUpdatingFromHybris(consignmentDetailsResponse.state, consignmentDetailsResponse.previousState);


    // If there is no consignment snapshot data, don't render the component
    if (!originalEntries || originalEntries.length === 0 || !consignmentSnapshot) return null;

    // Get Value from param for currency differentiate
    const queryParameters = new URLSearchParams(window.location.search);
    const country = queryParameters.get("country");

    // Create a single object for the OrderSummary props to avoid duplication
    const summaryPropsSubmittedOrder = {
        country,
        subTotal: consignmentDetailsResponse.subTotal,
        totalTax: consignmentDetailsResponse.totalTax,
        totalSurcharges: consignmentDetailsResponse.totalSurcharges,
        totalDeliveryCost: consignmentDetailsResponse.totalDeliveryCost,
        totalPriceWithTax: consignmentDetailsResponse.totalPriceWithTax,
        isPriceUpdating: isPriceUpdating
    };

    const summaryPropsOriginalOrder = {
        country,
        subTotal: consignmentSnapshot.subTotal,
        totalTax: consignmentSnapshot.totalTax,
        totalSurcharges: consignmentSnapshot.totalSurcharges,
        totalDeliveryCost: consignmentSnapshot.totalDeliveryCost,
        totalPriceWithTax: consignmentSnapshot.totalPriceWithTax,
    };

    const getTitle = (index: number) => {

        if (isModalView && index === 0) {
            return TEXT_LABELS.MODIFIED_ORDER;
        }

        if (!isModalView && index === 0) {
            // return consignmentDetailsResponse.state === MODIFIED_BY_CUSTOMER ? TEXT_LABELS.SUBMITTED_BY_CUSTOMER : TEXT_LABELS.SUBMITTED_BY_VENDOR;
            // FIX: Bug Id: 5696184
            return TEXT_LABELS.SUBMITTED_FOR_APPROVAL;
        }

        return '';
    }

    const orderHistory = (
        <>
            {isModalView && showRentDates(consignmentDetailsResponse.rentalStartDate, consignmentDetailsResponse.rentalEndDate)}
            {
                groupedSubmittedEntries.map((item, index) => (
                    <OrderAddonTablePreview
                        products={item!}
                        country={country}
                        backgroundColor='#fff'
                        title={getTitle(index)}
                    />
                ))
            }
            <OrderSummary {...summaryPropsSubmittedOrder} />

            {isModalView && showRentDates(consignmentSnapshot.rentalStartDate, consignmentSnapshot.rentalEndDate)}
            {
                groupedOriginalEntries.map((item, index) => (
                    <OrderAddonTablePreview
                        products={item!}
                        country={country}
                        backgroundColor='#fff'
                        title={index === 0 ? TEXT_LABELS.ORIGINAL_ORDER : ''}
                    />
                ))
            }
            <OrderSummary {...summaryPropsOriginalOrder} />
        </>
    );

    return (
        <>
            <FlexboxContainerComponent justifyContent='end' alignItems='end' >
                <Button
                    onClick={() => printComponent('order-snapshot-printable-content', `Order Changes History ${parseConsignmentId(consignmentDetailsResponse.consignmentId)}`, 'Order Changes History')}
                    leftIcon={() => <PrintIcon />}
                    background="white"
                    borderColor="white"
                    disabled={isPriceUpdating}
                />
            </FlexboxContainerComponent>

            {orderHistory}

            <div id='order-snapshot-printable-content' style={{ display: 'none' }}>
                {orderHistory}
            </div>
        </>
    );
};

export default OrderSnapshotPreview;